/** What a person can do with a filing from its page: Preview, Send for approval, and under More the
 * Export and Email controls - ADR-0212 decision 6, and the 2026-11-03 header review.
 *
 * **Nothing here sends anything to a regulator, and no word on it says so.** One info control covers
 * the group, where there used to be one beside every button. Each control is the existing act:
 * - *Preview* is the rehearsal render (ADR-0208): HTML the browser draws, marked as a rehearsal, with
 *   no fingerprint. A link, not a request this screen makes.
 * - *Send for approval* is the approval request flow on the filing's record: a route, a named
 *   reviewer, ordered stages. In the app only. It opens a drawer **on the filing**
 *   (`SendForApprovalDrawer`), offered to the roles that may submit and absent for the rest (design
 *   rule 6: no control that predictably 403s).
 * - **Once a request is open the button is gone, and the header says where the sign-off is**
 *   (design review 2026-11-03, persona 4): *Review and decide* to the person whose stage it is at, who
 *   used to be shown a button to send what had been sent; *Open the sign-off* to anyone else who may
 *   read it. Both go to the filing's Workflow tab, where the decision is made (the page never decides).
 * - *Export* applies to an **issued** filing - the frozen record - and offers its page, CSV and
 *   workbook, and a PDF **only** when `GET /artifacts/pdf-capability` says a renderer exists. Before
 *   anything is issued the item is off and says why beside it.
 * - *Audit pack* is one archive for an examiner: the verification pack, an evidence index, the filing's
 *   approvals, the assurance queue about it and the source census. A download link for an Admin, an
 *   Approver or an Auditor (the server's list; `mayDownloadAuditPack` mirrors it) and a disabled item
 *   saying who can for the rest.
 * - *Email the export* is off, and says so, until a notification sender is set up.
 */

import { DownloadSimple, Envelope, Eye, FileZip, PaperPlaneTilt, SealCheck } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { apiUrl } from "../../api/client";
import { maySubmit } from "../../api/approvals";
import { usePdfCapability } from "../../api/pdfCapability";
import { rehearsalUrl } from "../../api/reportReviews";
import { auditPackUrl, mayDownloadAuditPack } from "../../api/queries";
import type { Artifact, Scope2Method } from "../../api/queries";
import { Drawer } from "../../components/ui/Drawer";
import { InfoPopover } from "../../components/ui/InfoPopover";
import { FilingPdf } from "../FilingPdf";
import styles from "./Filings.module.css";
import { MoreMenu } from "./MoreMenu";

function ExportDrawer({
  open,
  onClose,
  latest,
}: {
  open: boolean;
  onClose: () => void;
  latest: Artifact;
}) {
  const capability = usePdfCapability(open);
  return (
    <Drawer open={open} onClose={onClose} title="Export this filing" width="sm" testId="export-drawer">
      <ul className={styles.drawerList}>
        <li>
          <a className={styles.fix} href={apiUrl(`/api/v1/artifacts/${latest.id}/view`)} target="_blank" rel="noreferrer">
            Page
          </a>
        </li>
        <li>
          <a className={styles.fix} href={apiUrl(`/api/v1/artifacts/${latest.id}/csv`)} download>
            CSV
          </a>
        </li>
        <li>
          <a className={styles.fix} href={apiUrl(`/api/v1/artifacts/${latest.id}/xlsx`)} download data-testid="filing-xlsx">
            Workbook
          </a>
        </li>
        <li>
          {capability.data?.available ? (
            <FilingPdf filingId={latest.id} />
          ) : (
            <>
              <span className={styles.refused} data-testid="pdf-not-offered">
                PDF
              </span>
              <InfoPopover infoKey="filings.export" />
            </>
          )}
        </li>
      </ul>
    </Drawer>
  );
}

export function FilingActions({
  obligationId,
  method,
  role,
  latestIssued,
  onSend,
  signOff = "none",
}: {
  obligationId: string;
  method: Scope2Method;
  role: string | undefined;
  /** The newest issued filing, or undefined when nothing has been issued. */
  latestIssued: Artifact | undefined;
  /** Opens the send-for-approval drawer, which the page owns so a to-do row can open it too. */
  onSend: () => void;
  /** Where the filing's sign-off stands for this person: `none` (nothing sent: offer Send), `mine` (a
   * request is at a stage whose reviewer they are), or `open` (a request is open and is somebody else's). */
  signOff?: "none" | "mine" | "open";
}) {
  const [exporting, setExporting] = useState(false);
  return (
    <div className={styles.actionRow}>
      <a
        className={styles.button}
        href={rehearsalUrl(obligationId, method)}
        target="_blank"
        rel="noreferrer"
        data-testid="filing-preview"
      >
        <Eye size={16} aria-hidden="true" />
        Preview
      </a>

      {signOff !== "none" ? (
        <Link
          className={styles.button}
          to="/records/$recordId"
          params={{ recordId: obligationId }}
          search={{ method, tab: "workflow" }}
          data-testid={signOff === "mine" ? "filing-decide" : "filing-open-signoff"}
        >
          <SealCheck size={16} aria-hidden="true" />
          {signOff === "mine" ? "Review and decide" : "Open the sign-off"}
        </Link>
      ) : maySubmit(role) ? (
        <button type="button" className={styles.button} onClick={onSend} data-testid="filing-send-review">
          <PaperPlaneTilt size={16} aria-hidden="true" />
          Send for approval
        </button>
      ) : null}

      <MoreMenu
        items={[
          {
            key: "export",
            label: "Export",
            icon: DownloadSimple,
            ...(latestIssued ? { onSelect: () => setExporting(true) } : { reason: "After it is issued" }),
            testId: "filing-export",
          },
          {
            // One download for an examiner (feature 1): offered to the three roles the server admits and,
            // to the others, drawn off with who can - never a link that is certain to be refused. Before
            // anything is issued too: an assurance provider asks while the figures are still moving.
            key: "audit-pack",
            label: "Audit pack",
            icon: FileZip,
            ...(mayDownloadAuditPack(role)
              ? { href: auditPackUrl(obligationId, method) }
              : { reason: "Admin, Approver, Auditor" }),
            testId: "filing-audit-pack",
          },
          {
            key: "email",
            label: "Email the export",
            icon: Envelope,
            reason: "Not set up",
            testId: "filing-email",
          },
        ]}
      />
      <InfoPopover infoKey="filings.actions" />

      {latestIssued ? (
        <ExportDrawer open={exporting} onClose={() => setExporting(false)} latest={latestIssued} />
      ) : null}
    </div>
  );
}
